'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { VideoStyle, VideoLook } from '@/.gql/graphql';
import EffectIcon from '@/public/icons/local_fire_department.svg';
import StyleIcon from '@/public/icons/person.svg';
import PhotoIcon from '@/public/icons/photo_camera_back.svg';
import VideoIcon from '@/public/icons/play_circle.svg';
import { useProfileContext, useStyleContext } from '@/src/providers';
import { getImageByGender } from '@/src/utils/getImageByGender';
import { PromoLabel } from '@/src/components/PromoLabel';

import { EmblaPaginationDots } from './EmblaPaginationDots';

const styles = {
  carouselContainer: 'relative overflow-hidden w-full aspect-square',
  carousel: 'flex duration-700 cursor-pointer',
  imageWrap:
    'relative shrink-0 flex flex-col items-center pb-9 mb-5 justify-end w-full aspect-square overflow-hidden',
  image:
    'absolute w-full h-full top-0 left-0 object-cover object-top pointer-events-none',
  horizontalVideo:
    'absolute w-full top-1/2 -translate-y-1/2 left-0 object-cover object-top pointer-events-none aspect-[16/9]',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-xl',
  name: 'relative z-3 text-2xl font-bold mb-3',
  description: 'relative z-3 flex items-center gap-1',
  tag: 'absolute top-[60px] right-5',
  icon: 'w-5 h-5 text-label-secondary',
  iconStyle: 'w-4 h-4 text-label-secondary',
  count: 'relative text-base font-medium text-label-secondary',
};

export const EmblaCarousel = () => {
  const t = useTranslations('imageModal');
  const m = useTranslations('MenuPage');
  const {
    genderMode,
    portalSeeAllMode,
    activeStyle,
    currentPack,
    changeActiveStyle,
  } = useStyleContext();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    duration: 700,
    startIndex: currentPack.findIndex((item) => item.id === activeStyle?.id),
  });
  const [selectedIndex, setSelectedIndex] = useState(
    currentPack.findIndex((item) => item.id === activeStyle?.id),
  );
  const profile = useProfileContext()!;
  const [mediaLoaded, setMediaLoaded] = useState<boolean[]>([]);

  const videoRefs = useRef<
    { bg?: HTMLVideoElement | null; main?: HTMLVideoElement | null }[]
  >([]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      setSelectedIndex(index);
      changeActiveStyle(currentPack[index]?.id, portalSeeAllMode);
    };
    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, currentPack, changeActiveStyle, portalSeeAllMode]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        emblaApi.scrollPrev();
      } else if (event.key === 'ArrowRight') {
        emblaApi.scrollNext();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [emblaApi]);

  useEffect(() => {
    const refs = videoRefs.current[selectedIndex];
    const mainVideo = refs?.main;
    const bgVideo = refs?.bg;

    if (mainVideo) {
      mainVideo.play();
    }
    if (bgVideo) {
      bgVideo.play();
    }

    return () => {
      if (mainVideo) {
        mainVideo.pause();
        // @ts-expect-error Chrome and Safari expect string for currentTime
        mainVideo.currentTime = '0';
      }
      if (bgVideo) {
        bgVideo.pause();
        // @ts-expect-error Chrome and Safari expect string for currentTime
        bgVideo.currentTime = '0';
      }
    };
  }, [selectedIndex]);

  const description = useMemo(() => {
    switch (activeStyle?.__typename) {
      case 'Style':
        return (
          <>
            <StyleIcon className={styles.iconStyle} />
            <div className={styles.count}>
              {t('title')}
              {': '} {m('photosCount')}
            </div>
          </>
        );
      case 'PhotoSession':
        return (
          <>
            <PhotoIcon className={styles.icon} />
            <div className={styles.count}>
              {t('photoshootTitle')}
              {': '} {m('photoshootPhotosCount')}
            </div>
          </>
        );
      case 'VideoVeo3':
        return (
          <>
            <VideoIcon className={styles.icon} />
            <div className={styles.count}>
              {m('videoVeo3')}
              {': '} {m('videoCount')}
            </div>
          </>
        );
      case 'StoryLine':
        return (
          <>
            <VideoIcon className={styles.icon} />
            <div className={styles.count}>{m('storyLines')}</div>
          </>
        );
      case 'VideoLook':
        return (
          <>
            <VideoIcon className={styles.icon} />
            <div className={styles.count}>
              {!(activeStyle as VideoLook).styleCategoryId &&
                `${t('videoTitle')}: `}
              {m('videoCount')}
            </div>
          </>
        );
      case 'VideoStyle':
        return (
          <>
            <VideoIcon className={styles.icon} />
            <div className={styles.count}>
              {!(activeStyle as VideoStyle).styleCategoryId &&
                `${t('videoTitle')}: `}
              {m('videoCount')}
            </div>
          </>
        );
      case 'Effect':
        return activeStyle?.isStickerPack ? (
          <>
            <EffectIcon className={styles.icon} />
            <div className={styles.count}>{t('stickerpackTitle')}</div>
          </>
        ) : // <>
        //   <EffectIcon className={styles.icon} />
        //   <div className={styles.count}>
        //     {t('effectTitle')}
        //     {': '} {m('effectCount')}
        //   </div>
        // </>
        null;
      default:
        return;
    }
  }, [activeStyle, m, t]);

  return (
    <div className={styles.carouselContainer} ref={emblaRef}>
      <div className={styles.carousel}>
        {currentPack.map((item, index) => (
          <div key={item.id} className={styles.imageWrap}>
            {!mediaLoaded[index] && (
              <div className="absolute z-2 inset-0 animate-skeleton-pulse"></div>
            )}
            {getImageByGender(item, genderMode)?.includes('.mp4') ? (
              <React.Fragment key={getImageByGender(item, genderMode)!}>
                {['VideoVeo3', 'StoryLine'].includes(item?.__typename!) ? (
                  <video
                    preload="metadata"
                    className="absolute top-0 left-0 w-full h-full object-cover scale-110 blur-lg z-0"
                    loop
                    muted
                    playsInline
                    webkit-playsinline="true"
                    ref={(el) => {
                      if (!videoRefs.current[index]) {
                        videoRefs.current[index] = {};
                      }
                      videoRefs.current[index].bg = el;
                    }}
                  >
                    <source src={getImageByGender(item, genderMode)!} />
                  </video>
                ) : null}

                <video
                  ref={(el) => {
                    if (!videoRefs.current[index]) {
                      videoRefs.current[index] = {};
                    }
                    videoRefs.current[index].main = el;
                  }}
                  preload="metadata"
                  loop
                  muted
                  className={
                    ['VideoVeo3', 'StoryLine'].includes(item?.__typename!)
                      ? styles.horizontalVideo
                      : styles.image
                  }
                  playsInline
                  webkit-playsinline="true"
                  onLoadedData={() => {
                    setMediaLoaded((prev) => {
                      const next = [...prev];
                      next[index] = true;
                      return next;
                    });
                  }}
                >
                  <source src={getImageByGender(item, genderMode)!} />
                </video>
              </React.Fragment>
            ) : (
              <Image
                fill
                priority={index === selectedIndex}
                src={getImageByGender(item, genderMode)!}
                alt={item.name[profile?.lang]}
                className={styles.image}
                quality={95}
                loading={index === selectedIndex ? undefined : 'lazy'}
                onLoad={() => {
                  setMediaLoaded((prev) => {
                    const next = [...prev];
                    next[index] = true;
                    return next;
                  });
                }}
              />
            )}
            <div className={styles.gradient} />
            {item?.isNew && (
              <Image
                width={42}
                height={23}
                src="/icons/label-new.svg"
                alt="tag"
                className={styles.tag}
              />
            )}
            {item?.isPopular && !item?.isNew && (
              <Image
                width={42}
                height={23}
                src="/icons/label-top.svg"
                alt="tag"
                className={styles.tag}
              />
            )}
            <PromoLabel
              item={item}
              className={twMerge(
                !item?.isPopular && !item?.isNew
                  ? styles.tag
                  : 'absolute top-24 right-5',
              )}
            />
            <div className={styles.name}>{item?.name[profile?.lang]}</div>
            <div className={styles.description}>{description}</div>
          </div>
        ))}
      </div>
      {emblaApi && (
        <EmblaPaginationDots
          totalSlides={currentPack.length}
          selectedIndex={selectedIndex}
        />
      )}
    </div>
  );
};
